import type { BaseQueryFn } from '@reduxjs/toolkit/query'
import { getCache, setCache } from 'DataBase/rtk-persist/cache'
import { CacheStrategy, type PersistRules } from 'DataBase/rtk-persist/types'
import { createCacheKey } from 'DataBase/rtk-persist/utils'

export function createPersistentBaseQuery(
  baseQuery: BaseQueryFn,
  {
    persistRules,
    dbName,
    storeName,
    version,
  }: { persistRules: PersistRules; dbName: string; storeName: string; version: number },
): BaseQueryFn {
  return async (args, api, extraOptions) => {
    const endpoint = api.endpoint

    const rule = persistRules[endpoint as keyof typeof persistRules]

    if (!rule) {
      return baseQuery(args, api, extraOptions)
    }

    const key = createCacheKey(endpoint, args)

    const cached = await getCache(key, { version, dbName, storeName })

    // cache-first

    if (rule.strategy === CacheStrategy.CacheFirst && cached) {
      return {
        data: cached.data,
      }
    }

    // stale while revalidate

    if (rule.strategy === CacheStrategy.StaleWhileRevalidate && cached) {
      const refreshInBackground = async () => {
        const result = await baseQuery(args, api, extraOptions)

        if ('data' in result) {
          await setCache(
            {
              key,
              endpoint,
              version: version,
              createdAt: Date.now(),
              expiresAt: Date.now() + rule.ttl,
              data: result.data,
            },
            dbName,
            storeName,
          )
        }
      }

      void refreshInBackground()

      return {
        data: cached.data,
      }
    }

    const result = await baseQuery(args, api, extraOptions)

    if ('error' in result) {
      if (rule.strategy === CacheStrategy.NetworkFirst && cached) {
        return {
          data: cached.data,
        }
      }

      return result
    }

    await setCache(
      {
        key,
        endpoint,
        version: version,
        createdAt: Date.now(),
        expiresAt: Date.now() + rule.ttl,
        data: result.data,
      },
      dbName,
      storeName,
    )

    return result
  }
}
